/**
 * Admin Service
 * Handles all admin-related API calls
 */

const API_URL = import.meta.env.VITE_API_URL;

export interface CreateInviteRequest {
  email?: string;
  expiresInDays?: number;
  maxUses?: number;
  notes?: string;
}

export interface InviteCode {
  inviteId: string;
  code: string;
  createdBy?: string;
  createdAt: string;
  expiresAt: string;
  status: 'PENDING' | 'USED' | 'EXPIRED' | 'REVOKED';
  usedBy?: string;
  usedAt?: string;
  maxUses: number;
  currentUses: number;
  email?: string;
  notes?: string;
}

export interface ListInvitesResponse {
  items: InviteCode[];
  nextCursor?: string;
  hasMore: boolean;
}

class AdminService {
  /**
   * Create a new invite code
   */
  async createInvite(token: string, data: CreateInviteRequest): Promise<InviteCode> {
    const response = await fetch(`${API_URL}/admin/invites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create invite code');
    }

    return response.json();
  }

  /**
   * List invite codes
   */
  async listInvites(
    token: string,
    params?: {
      status?: 'PENDING' | 'USED' | 'EXPIRED' | 'REVOKED';
      limit?: number;
      cursor?: string;
    }
  ): Promise<ListInvitesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.cursor) queryParams.append('cursor', params.cursor);

    const url = `${API_URL}/admin/invites${queryParams.toString() ? `?${queryParams}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to list invite codes');
    }

    return response.json();
  }
}

export const adminService = new AdminService();
